import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaCar, 
  FaParking, 
  FaMapMarkerAlt, 
  FaClock, 
  FaMoneyBillWave, 
  FaShieldAlt, 
  FaMobileAlt, 
  FaRobot, 
  FaEnvelope, 
  FaPhone, 
  FaMapPin, 
  FaChevronRight,
  FaChevronLeft,
  FaArrowRight,
  FaBolt,
  FaPlane,
  FaTrain,
  FaHospital,
  FaCity,
  FaHotel,
  FaCalendarAlt,
  FaCarSide,
  FaFilter,
  FaStar,
  FaCheckCircle,
  FaSearch,
  FaSpinner,
  FaExclamationTriangle,
  FaUsers,
  FaAward,
  FaEye,
  FaLock,
  FaSignInAlt
} from 'react-icons/fa';
import './mainpage.css';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (inView) {
      let startTime = null;
      const animate = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [end, duration, inView]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return num;
  };

  return <span ref={ref}>{formatNumber(count)}{suffix}</span>;
};

const ParksyHomepage = () => {
  // API Configuration
  const API_BASE_URL = "https://parksy-backend.onrender.com";
  const navigate = useNavigate();
  
  // Refs for auto-scrolling
  const resultsRef = useRef(null);
  
  // State Management
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentBg, setCurrentBg] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  
  // API State
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [availableAirports, setAvailableAirports] = useState([]);
  
  // Search Parameters
  const [searchParams, setSearchParams] = useState({
    airport_code: "LHR",
    dropoff_date: new Date(Date.now() + 2 * 86400000).toISOString().split('T')[0],
    dropoff_time: "09:00",
    pickup_date: new Date(Date.now() + 9 * 86400000).toISOString().split('T')[0],
    pickup_time: "18:00"
  });

  // Background images
  const backgrounds = [
    'https://images.unsplash.com/photo-1590674899484-d5640e854abe?q=80&w=1467&auto=format&fit=crop&ixlib=rb-4.1.0',
    'https://images.unsplash.com/photo-1635704764831-082c47202c6c?q=80&w=1473&auto=format&fit=crop&ixlib=rb-4.1.0',
    'https://images.unsplash.com/photo-1561310727-11876015eb33?q=80&w=1032&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'https://images.unsplash.com/photo-1689166373309-029c101b6a40?q=80&w=1203&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0'
  ];

  // Popular locations
  const popularLocations = [
    { name: "London", icon: <FaCity />, query: "london" },
    { name: "Manchester", icon: <FaCity />, query: "manchester" },
    { name: "Birmingham", icon: <FaCity />, query: "birmingham" },
    { name: "Leeds", icon: <FaCity />, query: "leeds" },
    { name: "Airports", icon: <FaPlane />, query: "airport" },
    { name: "Train Stations", icon: <FaTrain />, query: "train station" },
    { name: "Hospitals", icon: <FaHospital />, query: "hospital" },
    { name: "Hotels", icon: <FaHotel />, query: "hotel" }
  ];

  // Testimonials
  const testimonials = [
    {
      id: 1,
      text: "I used to dread the stress of finding a spot before a flight. With Parksy, I booked a space in the long-stay car park weeks in advance and saved over 50% compared to the drive-up rate. Total peace of mind!",
      name: "James Wilson",
      role: "Daily Commuter",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80"
    },
    {
      id: 2,
      text: "As a holidaymaker, airport parking is always a huge expense. Parksy's AI found me a secure, off-airport lot with a free shuttle that was £120 cheaper for my two-week trip. Amazing!",
      name: "Sarah Chen",
      role: "Property Owner",
      avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80"
    },
    {
      id: 3,
      text: "I rent out my land near the airport. Parksy handles all the bookings and payments. It's now a completely passive income that earned me £800 last month from travelers parking their cars",
      name: "Mohammed Khan",
      role: "Delivery Driver",
      avatar: "https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=634&q=80"
    }
  ];

  // Stats data
  const statsData = [
    { number: 250000, displayNumber: "250K+", label: "Happy Drivers", icon: <FaUsers /> },
    { number: 15000, displayNumber: "15K+", label: "Parking Spots", icon: <FaParking /> },
    { number: 98, displayNumber: "98%", label: "Success Rate", icon: <FaAward /> }
  ];

  // Features data
  const featuresData = [
    {
      icon: <FaParking />,
      title: "Real-Time Availability",
      description: "Live parking data updated every minute across thousands of locations in the UK"
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Best Prices Guaranteed",
      description: "Compare prices from multiple providers and get the best deals with transparent pricing"
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Booking",
      description: "Your booking is protected with secure payments and instant confirmation"
    },
    {
      icon: <FaMobileAlt />,
      title: "Mobile Optimized",
      description: "Book parking on the go with our responsive platform that works on any device"
    },
    {
      icon: <FaEye />,
      title: "Smart Recommendations",
      description: "AI-powered suggestions to find the perfect parking spot based on your preferences"
    },
    {
      icon: <FaLock />,
      title: "Trusted Platform",
      description: "Join thousands of satisfied customers who trust Parksy for their parking needs"
    }
  ];

  // Check if user is logged in
  const isLoggedIn = () => {
    const token = localStorage.getItem('token');
    return token !== null;
  };

  // Handle booking button click
  const handleBookingClick = (result) => {
    if (!isLoggedIn()) {
      // Redirect to login with booking info
      navigate('/login', { 
        state: { 
          message: 'Please login to complete your booking',
          from: { pathname: '/home' },
          bookingData: result
        }
      });
    } else {
      // Proceed with booking logic
      console.log('Proceeding with booking:', result);
      // Here you can add your booking logic
      // navigate('/booking', { state: { parkingData: result } });
    }
  };

  // Scroll to results function
  const scrollToResults = () => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  // Test Backend Connection
  const testBackendConnection = async () => {
    setConnectionStatus('testing');
    try {
      const response = await fetch(`${API_BASE_URL}/api/parking/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Backend connected:', data);
        setConnectionStatus('connected');
        return true;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Connection failed:', error.message);
      setConnectionStatus('failed');
      setApiError(`Backend server error: ${error.message}`);
      return false;
    }
  };

  // Load Available Airports
  const loadAirports = async () => {
    try {
      const isConnected = await testBackendConnection();
      if (!isConnected) return;

      const response = await fetch(`${API_BASE_URL}/api/parking/airports`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailableAirports(data.data);
          console.log('Airports loaded:', data.data.length);
        } else {
          throw new Error('Failed to load airports');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error loading airports:', error);
      setApiError(error.message);
    }
  };

  // Search Parking API
  const searchParking = useCallback(async () => {
    if (connectionStatus !== 'connected') {
      setApiError('Backend server is not connected. Please check if the server is running.');
      return;
    }

    setIsLoading(true);
    setApiError(null);
    
    try {
      console.log('Searching parking with params:', searchParams);
      
      const response = await fetch(`${API_BASE_URL}/api/parking/search-parking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchParams)
      });

      console.log('Search API Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Response Error:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('Search API Response:', result);
      
      if (result.success && result.data && result.data.products) {
        const processedProducts = result.data.products.map(product => ({
          ...product,
          features_array: product.features_array || [],
          availability_status: product.available_spaces 
            ? `${product.available_spaces} spots available`
            : 'Available Now',
          last_updated: new Date().toLocaleTimeString(),
          formatted_price: parseFloat(product.price).toFixed(2),
          commission_amount: (parseFloat(product.price) * (parseFloat(product.share_percentage || 0) / 100)).toFixed(2),
        }));
        
        setSearchResults(processedProducts);
        setApiError(null);
        console.log('Products processed and loaded:', processedProducts.length);
        
        // Auto-scroll to results after successful search
        setTimeout(() => {
          scrollToResults();
        }, 100);
        
      } else {
        console.error('Invalid API response:', result);
        throw new Error(result.message || 'No parking data available from API');
      }
    } catch (error) {
      console.error('Error fetching products:', {
        message: error.message,
        name: error.name
      });
      setApiError(`Search failed: ${error.message}`);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchParams, connectionStatus, API_BASE_URL]);

  // Handle Search Submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (connectionStatus === 'connected') {
      searchParking();
    }
  };

  // Handle Search Parameter Change
  const handleSearchParamChange = (field, value) => {
    setSearchParams(prev => ({ ...prev, [field]: value }));
  };

  // Navigation Functions
  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Initialize Effects
  useEffect(() => {
    const videoTimer = setTimeout(() => {
      setShowWelcomeVideo(false);
    }, 3000);

    const bgInterval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % backgrounds.length);
    }, 5000);
    
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 7000);
    
    return () => {
      clearTimeout(videoTimer);
      clearInterval(bgInterval);
      clearInterval(testimonialInterval);
    };
  }, []);

  useEffect(() => {
    loadAirports();
  }, []);

  return (
    <>
      {/* Welcome Video Animation */}
      <AnimatePresence>
        {showWelcomeVideo && (
          <motion.div
            className="parksy-welcome-video-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <video 
              autoPlay 
              muted 
              loop
              playsInline
              preload="auto"
              className="parksy-fullscreen-video"
            >
              <source src="/aeroplaneanimation.mp4" type="video/mp4" />
            </video>
            
            <div className="parksy-video-overlay"></div>
            
            <div className="parksy-welcome-content">
              <div className="parksy-welcome-text-wrapper">
                <div className="parksy-welcome-title">Welcome to Parksy</div>
                <div className="parksy-welcome-subtitle">Premium Airport Parking Solutions</div>
                <div className="parksy-animation-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Homepage */}
      <div className="parksy-main-homepage">
        {/* Hero Section with Search */}
        <section className="parksy-hero-section">
          <div className="parksy-hero-backgrounds">
            {backgrounds.map((bg, index) => (
              <motion.div
                key={index}
                className="parksy-hero-bg-image"
                style={{ backgroundImage: `url(${bg})` }}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: currentBg === index ? 1 : 0,
                  scale: currentBg === index ? 1 : 1.05,
                }}
                transition={{ duration: 1.5, ease: [0.6, -0.05, 0.01, 0.99] }}
              />
            ))}
          </div>

          <div className="parksy-hero-overlay" />

          <motion.div
            className="parksy-hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="parksy-hero-title"
            >
              Find Perfect Parking in Seconds
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="parksy-hero-subtitle"
            >
              Real-time Airport parking availability across the UK with secure booking and guaranteed spots
            </motion.p>

            {/* Main Search Box */}
            <motion.div
              className="parksy-main-search-container"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              {/* Connection Status */}
              {connectionStatus === 'failed' && (
                <div className="parksy-api-status-banner parksy-status-error">
                  <FaExclamationTriangle />
                  <span>Backend Connection Failed - Check if server is running</span>
                </div>
              )}
              
              {connectionStatus === 'connected' && (
                <div className="parksy-api-status-banner parksy-status-success">
                  <FaCheckCircle />
                  <span>Live Connection - Real-time parking data ready</span>
                </div>
              )}

              <form onSubmit={handleSearchSubmit} className="parksy-search-form">
                {/* Search Parameters */}
                <div className="parksy-search-parameters">
                  <div className="parksy-param-group">
                    <label className="parksy-param-label">
                      <FaPlane />
                      Airport
                    </label>
                    <select
                      className="parksy-param-input"
                      value={searchParams.airport_code}
                      onChange={(e) => handleSearchParamChange('airport_code', e.target.value)}
                      disabled={connectionStatus !== 'connected'}
                    >
                      {availableAirports.map(airport => (
                        <option key={airport.code} value={airport.code}>
                          {airport.name} ({airport.code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="parksy-param-group">
                    <label className="parksy-param-label">
                      <FaCalendarAlt />
                      Drop-off
                    </label>
                    <input
                      type="date"
                      className="parksy-param-input"
                      value={searchParams.dropoff_date}
                      onChange={(e) => handleSearchParamChange('dropoff_date', e.target.value)}
                      min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      disabled={connectionStatus !== 'connected'}
                    />
                  </div>

                  <div className="parksy-param-group">
                    <label className="parksy-param-label">
                      <FaClock />
                      Time
                    </label>
                    <input
                      type="time"
                      className="parksy-param-input"
                      value={searchParams.dropoff_time}
                      onChange={(e) => handleSearchParamChange('dropoff_time', e.target.value)}
                      disabled={connectionStatus !== 'connected'}
                    />
                  </div>

                  <div className="parksy-param-group">
                    <label className="parksy-param-label">
                      <FaCalendarAlt />
                      Pick-up
                    </label>
                    <input
                      type="date"
                      className="parksy-param-input"
                      value={searchParams.pickup_date}
                      onChange={(e) => handleSearchParamChange('pickup_date', e.target.value)}
                      min={searchParams.dropoff_date}
                      disabled={connectionStatus !== 'connected'}
                    />
                  </div>

                  <div className="parksy-param-group">
                    <label className="parksy-param-label">
                      <FaClock />
                      Time
                    </label>
                    <input
                      type="time"
                      className="parksy-param-input"
                      value={searchParams.pickup_time}
                      onChange={(e) => handleSearchParamChange('pickup_time', e.target.value)}
                      disabled={connectionStatus !== 'connected'}
                    />
                  </div>

                  <button
                    type="submit"
                    className="parksy-search-button"
                    disabled={isLoading || connectionStatus !== 'connected'}
                  >
                    {isLoading ? (
                      <>
                        <FaSpinner className="parksy-spinning" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <FaSearch />
                        Search Live
                      </>
                    )}
                  </button>
                </div>

                {/* Quick Location Tags */}
                <div className="parksy-quick-locations">
                  <span className="parksy-quick-label">Popular:</span>
                  {popularLocations.slice(0, 6).map((location, index) => (
                    <button
                      key={index}
                      type="button"
                      className="parksy-location-tag"
                      onClick={() => setSearchQuery(location.query)}
                    >
                      {location.icon} {location.name}
                    </button>
                  ))}
                </div>
              </form>
            </motion.div>
          </motion.div>
        </section>

        {/* Stats Section */}
        <section className="parksy-stats-section">
          <div className="parksy-stats-container">
            <div className="parksy-stats-grid">
              {statsData.map((stat, index) => (
                <motion.div
                  key={index}
                  className="parksy-stat-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="parksy-stat-icon">
                    {stat.icon}
                  </div>
                  <div className="parksy-stat-content">
                    <div className="parksy-stat-number">
                      {stat.number === 98 ? (
                        <><AnimatedCounter end={stat.number} suffix="%" /></>
                      ) : (
                        <AnimatedCounter end={stat.number} suffix="+" />
                      )}
                    </div>
                    <div className="parksy-stat-label">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Search Results Section */}
        {(searchResults.length > 0 || isLoading || apiError) && (
          <section ref={resultsRef} className="parksy-search-results-section">
            <div className="parksy-results-container">
              <h2 className="parksy-results-title">Live Parking Results</h2>
              
              {isLoading && (
                <div className="parksy-loading-state">
                  <FaSpinner className="parksy-spinning parksy-large-spinner" />
                  <h3>Loading Real-Time Data...</h3>
                  <p>Fetching live availability from API</p>
                </div>
              )}

              {apiError && (
                <div className="parksy-error-state">
                  <FaExclamationTriangle />
                  <h3>Search Error</h3>
                  <p>{apiError}</p>
                  <button onClick={searchParking} className="parksy-retry-button">
                    Try Again
                  </button>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="parksy-results-grid">
                  {searchResults.map((result, index) => (
                    <motion.div 
                      key={index} 
                      className="parksy-result-card"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      whileHover={{ y: -8, scale: 1.02 }}
                    >
                      <div className="parksy-card-header">
                        <h4>{result.name}</h4>
                        <div className="parksy-live-badge">
                          <span className="parksy-pulse-dot"></span>
                          LIVE
                        </div>
                      </div>
                      <div className="parksy-card-content">
                        <div className="parksy-parking-type">{result.parking_type}</div>
                        <div className="parksy-price-display">
                          <span className="parksy-currency">£</span>
                          <span className="parksy-amount">{result.formatted_price}</span>
                        </div>
                        <div className="parksy-availability">{result.availability_status}</div>
                        <div className="parksy-features">
                          {result.features_array.slice(0, 3).map((feature, idx) => (
                            <span key={idx} className="parksy-feature-tag">
                              <FaCheckCircle />
                              {feature}
                            </span>
                          ))}
                        </div>
                        <div className="parksy-booking-info">
                          <span>Commission: {result.share_percentage}%</span>
                          <span>Updated: {result.last_updated}</span>
                        </div>
                      </div>
                      <button 
                        className="parksy-book-button"
                        onClick={() => handleBookingClick(result)}
                      >
                        {isLoggedIn() ? (
                          <>
                            <FaLock />
                            Book with Parksy 
                            <FaArrowRight />
                          </>
                        ) : (
                          <>
                            <FaSignInAlt />
                            Book with Parksy
                            <FaArrowRight />
                          </>
                        )}
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Features Section */}
        <section className="parksy-features-section">
          <div className="parksy-features-container">
            <motion.h2 
              className="parksy-features-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Why Choose Parksy?
            </motion.h2>
            
            <div className="parksy-features-grid">
              {featuresData.map((feature, index) => (
                <motion.div
                  key={index}
                  className="parksy-feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                >
                  <div className="parksy-feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="parksy-testimonials-section">
          <div className="parksy-testimonials-container">
            <motion.h2
              className="parksy-testimonials-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              What Our Users Say
            </motion.h2>

            <div className="parksy-testimonials-slider">
              <AnimatePresence mode="wait">
                <motion.div
                  key={testimonials[currentTestimonial].id}
                  className="parksy-testimonial-card"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="parksy-testimonial-content">
                    <div className="parksy-quote-icon">"</div>
                    <p className="parksy-testimonial-text">{testimonials[currentTestimonial].text}</p>
                    <div className="parksy-testimonial-author">
                      <img 
                        src={testimonials[currentTestimonial].avatar} 
                        alt={testimonials[currentTestimonial].name} 
                        className="parksy-author-avatar"
                      />
                      <div className="parksy-author-info">
                        <h4>{testimonials[currentTestimonial].name}</h4>
                        <p>{testimonials[currentTestimonial].role}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="parksy-slider-controls">
              <button className="parksy-slider-arrow parksy-prev" onClick={prevTestimonial}>
                <FaChevronLeft />
              </button>
              <div className="parksy-slider-dots">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    className={`parksy-dot ${currentTestimonial === i ? 'parksy-active' : ''}`}
                    onClick={() => setCurrentTestimonial(i)}
                  />
                ))}
              </div>
              <button className="parksy-slider-arrow parksy-next" onClick={nextTestimonial}>
                <FaChevronRight />
              </button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="parksy-contact-section">
          <div className="parksy-contact-container">
            <div className="parksy-contact-content">
              <motion.div
                className="parksy-contact-info"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2>Get In Touch</h2>
                <p>Have questions about parking or want to list your space? Our team is here to help.</p>
                
                <div className="parksy-contact-methods">
                  <div className="parksy-contact-method">
                    <div className="parksy-contact-icon">
                      <FaEnvelope />
                    </div>
                    <div className="parksy-contact-details">
                      <h4>Email Us</h4>
                      <a href="mailto:hello@parksy.uk">hello@parksy.uk</a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ParksyHomepage;